import { Button, Icon } from '@innovationnorway/cl-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GoalAccomplishmentDto } from 'src/api/v2';
import { useGoalReviewMutation } from '../hooks/use-goal-review';
import SivaGoalReviewForm from './SivaGoalReviewForm';
import { formatGoalReviewComment, parseGoalReviewComment } from 'src/features/siva/utils';

type Props = {
  clusterId: string;
  goal: GoalAccomplishmentDto;
  year: number;
  readMode?: boolean;
  setSivaHasSubmittedComment?: React.Dispatch<React.SetStateAction<boolean>>;
};

const SivaGoalReview: React.FC<Props> = ({ clusterId, goal, year, readMode, setSivaHasSubmittedComment }) => {
  const { t: tCommon } = useTranslation('common');
  const mutation = useGoalReviewMutation({
    clusterId,
    goalAccomplishmentId: goal.id,
    year: year,
  });

  const [showAddCommentForm, setShowAddCommentForm] = React.useState(false);
  const defaultValues = parseGoalReviewComment(goal.sivaComment, goal.expectedValue);
  const [sivaComment, setSivaComment] = React.useState<string>(defaultValues?.sivaComment);
  const [sivaCommentEv, setSivaCommentEv] = React.useState<number>(defaultValues?.expectedValue);

  function handleOnSubmit(formData: { comment: string; expectedValue: number }): void {
    const comment = formatGoalReviewComment({
      sivaComment: formData.comment,
      expectedValue: formData.expectedValue,
    });

    const promise = mutation.mutateAsync({
      sivaComment: comment,
      isApproved: false,
    });

    toast
      .promise(promise, {
        pending: 'Lagrer kommentar',
        success: 'Kommentar lagret',
        error: 'Kunne ikke lagre kommentar',
      })
      .then(() => {
        setShowAddCommentForm(false);
        setSivaComment(formData.comment);
        setSivaCommentEv(formData.expectedValue);
        if (setSivaHasSubmittedComment) setSivaHasSubmittedComment(true);
      });
  }

  if (showAddCommentForm) {
    return (
      <SivaGoalReviewForm
        key={showAddCommentForm ? 'show' : 'hide'}
        onSubmit={handleOnSubmit}
        onCancel={() => setShowAddCommentForm(false)}
        defaultValues={{ comment: defaultValues.sivaComment, expectedValue: defaultValues.expectedValue }}
        isLoading={mutation.isLoading}
      />
    );
  }

  if (sivaComment) {
    return (
      <div className="goal-feedback-card__notification">
        <span className="goal-feedback-card-notification-input__label">Kommentar fra SIVA</span>
        <p className="margin-top--0">{sivaComment}</p>

        <span className="goal-feedback-card-notification-input__label">Opprinnelig måltall</span>
        <p className="margin-top--0">{sivaCommentEv}</p>

        {!readMode && (
          <Button type="button" variant="tertiary" onClick={() => setShowAddCommentForm(true)}>
            <Icon prefix="fasr" iconName="edit" />
            Rediger kommentar og opprinnelig måltall
          </Button>
        )}
      </div>
    );
  }

  if (readMode) {
    return null;
  }

  return (
    <Button
      type="button"
      icon
      variant="tertiary"
      aria-label={tCommon('addComment')}
      onClick={() => setShowAddCommentForm(true)}
    >
      <Icon prefix="fasr" iconName="plus" />
    </Button>
  );
};

export default SivaGoalReview;
